import {
  CbEntitlementType,
  SetCbEntitlementInput,
} from 'api/graphql-global-types';
import { GetMyEntitlements_getMyEntitlements } from 'api/brandSubscriptions/types/GetMyEntitlements';
import { mapEntitlementTypenameToType } from './brands';

export type NumericEntitlementField = 'credits' | 'seats';

export type NumericEntitlementConfig = {
  type: CbEntitlementType;
  typename: 'CbTalentBookingResearchEntitlement' | 'CbTeamEntitlement';
  field: NumericEntitlementField;
  label: string;
  placeholder: string;
  visibleWhenMissing: boolean;
  min: number;
  requiredOnWrite: boolean;
};

export type ToggleEntitlementConfig = {
  type: CbEntitlementType;
  typename: 'CbRemoveMentionEntitlement' | 'CbMyTalentEntitlement';
};

export const NUMERIC_ENTITLEMENTS: NumericEntitlementConfig[] = [
  {
    type: CbEntitlementType.TalentBookingResearch,
    typename: 'CbTalentBookingResearchEntitlement',
    field: 'credits',
    label: 'Number of Credits',
    placeholder: 'Enter number of credits',
    visibleWhenMissing: true,
    min: 0,
    requiredOnWrite: false,
  },
  {
    type: CbEntitlementType.Team,
    typename: 'CbTeamEntitlement',
    field: 'seats',
    label: 'Number of Seats',
    placeholder: 'Enter total seat capacity',
    visibleWhenMissing: false,
    min: 1,
    requiredOnWrite: true,
  },
];

export const TOGGLE_ENTITLEMENTS: ToggleEntitlementConfig[] = [
  {
    type: CbEntitlementType.RemoveMention,
    typename: 'CbRemoveMentionEntitlement',
  },
  {
    type: CbEntitlementType.MyTalent,
    typename: 'CbMyTalentEntitlement',
  },
];

export const TOGGLE_ENTITLEMENT_TYPES: CbEntitlementType[] =
  TOGGLE_ENTITLEMENTS.map((config) => config.type);

export const findEntitlement = (
  entitlements: GetMyEntitlements_getMyEntitlements[],
  type: CbEntitlementType,
  typename: string
): GetMyEntitlements_getMyEntitlements | undefined =>
  entitlements.find(
    (item) => item.type === type && item.__typename === typename
  );

export const isEntitlementToggleEnabled = (
  entitlement?: GetMyEntitlements_getMyEntitlements | null
): boolean =>
  entitlement?.validUntil !== null && entitlement?.validUntil !== undefined;

export const getNumericEntitlementValue = (
  entitlement: GetMyEntitlements_getMyEntitlements | undefined,
  field: NumericEntitlementField
): number => {
  if (!entitlement) {
    return 0;
  }

  if (
    field === 'credits' &&
    entitlement.__typename === 'CbTalentBookingResearchEntitlement'
  ) {
    return entitlement.credits;
  }

  if (field === 'seats' && entitlement.__typename === 'CbTeamEntitlement') {
    return entitlement.seats ?? 0;
  }

  return 0;
};

export const parseNumericInput = (value?: string): number | null => {
  const trimmed = (value ?? '').trim();
  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
};

export const isNumericEntitlementVisible = (
  entitlements: GetMyEntitlements_getMyEntitlements[],
  config: NumericEntitlementConfig
): boolean =>
  config.visibleWhenMissing ||
  Boolean(findEntitlement(entitlements, config.type, config.typename));

type CollectEntitlementUpdatesArgs = {
  entitlements: GetMyEntitlements_getMyEntitlements[];
  entitlementDates: Record<string, string | null>;
  numericInputs: Record<string, string>;
  toggleValues: Record<string, boolean>;
  ownerId: string;
  performedById: string;
  toggleEnabledUntil: string;
};

export const collectEntitlementUpdates = ({
  entitlements,
  entitlementDates,
  numericInputs,
  toggleValues,
  ownerId,
  performedById,
  toggleEnabledUntil,
}: CollectEntitlementUpdatesArgs): SetCbEntitlementInput[] => {
  const updates = new Map<CbEntitlementType, SetCbEntitlementInput>();

  const addUpdate = (
    type: CbEntitlementType,
    fields: Partial<SetCbEntitlementInput>
  ) => {
    updates.set(type, {
      ownerId,
      performedById,
      type,
      ...updates.get(type),
      ...fields,
    });
  };

  entitlements
    .filter(
      (entitlement) => !TOGGLE_ENTITLEMENT_TYPES.includes(entitlement.type)
    )
    .forEach((entitlement) => {
      const currentDate = entitlementDates[entitlement.id];
      const originalValidUntil = entitlement.validUntil || null;

      if (currentDate !== originalValidUntil) {
        addUpdate(mapEntitlementTypenameToType(entitlement.__typename), {
          validUntil: currentDate || null,
        });
      }
    });

  NUMERIC_ENTITLEMENTS.forEach((config) => {
    const entitlement = findEntitlement(
      entitlements,
      config.type,
      config.typename
    );
    if (!entitlement) {
      return;
    }

    const parsed = parseNumericInput(numericInputs[config.type]);
    if (parsed === null) {
      return;
    }

    const original = getNumericEntitlementValue(entitlement, config.field);
    if (
      parsed !== original ||
      (config.requiredOnWrite && updates.has(config.type))
    ) {
      addUpdate(config.type, { [config.field]: parsed });
    }
  });

  TOGGLE_ENTITLEMENTS.forEach((config) => {
    const entitlement = findEntitlement(
      entitlements,
      config.type,
      config.typename
    );
    const shouldBeEnabled = Boolean(toggleValues[config.type]);

    if (entitlement) {
      const originalValidUntil = entitlement.validUntil || null;
      const originalIsEnabled =
        originalValidUntil !== null && originalValidUntil !== undefined;

      if (shouldBeEnabled === originalIsEnabled) {
        return;
      }

      addUpdate(config.type, {
        validUntil: shouldBeEnabled ? toggleEnabledUntil : null,
      });
      return;
    }

    if (shouldBeEnabled) {
      addUpdate(config.type, { validUntil: toggleEnabledUntil });
    }
  });

  return Array.from(updates.values());
};

export const getEntitlementInputError = (
  entitlements: GetMyEntitlements_getMyEntitlements[],
  numericInputs: Record<string, string>,
  updates: SetCbEntitlementInput[]
): string | null => {
  const invalidConfig = NUMERIC_ENTITLEMENTS.find((config) => {
    if (!findEntitlement(entitlements, config.type, config.typename)) {
      return false;
    }

    const parsed = parseNumericInput(numericInputs[config.type]);
    if (parsed === null) {
      return true;
    }

    const isWritten = updates.some((update) => update.type === config.type);
    return isWritten && parsed < config.min;
  });

  if (!invalidConfig) {
    return null;
  }

  return invalidConfig.min > 0
    ? `${invalidConfig.label} must be a whole number of at least ${invalidConfig.min}`
    : `${invalidConfig.label} must be a whole number`;
};
